import type { CSSProperties } from "react";
import { ImagePlus } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ScrollReveal } from "@/components/motion/ScrollReveal";
import { cn } from "@/lib/utils";
import {
  filledSponsorTiers,
  SPONSOR_TIER_IDS,
  sponsorTiers,
  stripSponsorTiers,
  type Sponsor,
  type SponsorTier,
  type SponsorTierId
} from "./data";

/*
 * Sponsor logo wall.
 *
 * Tier tone is carried by a DECORATIVE BAR ONLY — never by the heading text.
 * Measured against the white tile, the celebration accents fail AA badly
 * (accent-orange ~2.3:1, accent-teal ~2.4:1, accent ~1.9:1). PackagesGrid gets
 * away with tinted text because its tone sits on a tiny eyebrow beside a black
 * card title; here the tier name IS the content, so it stays text-foreground.
 *
 * Tailwind needs literal class names, so every tone/size string is spelled out
 * rather than templated (same convention as SponsorshipPage / PackagesGrid).
 */
// Exported so the sponsor-video cards on /sponsors/videos carry the same tone
// bar as the wall — one source of truth for "which colour is this tier".
export const TIER_TONES: Record<SponsorTierId, { bar: string }> = {
  // The event's owners → Bursa navy, the theme's secondary and what
  // SponsorshipPage's INKIND_TONE already uses for "outside the tier palette".
  organisers: { bar: "bg-secondary" },
  // Naming right, not a rung on the paid ladder → brand red, which is what the
  // codebase already uses for "outside the tier palette".
  officialBank: { bar: "bg-primary" },
  // The four below mirror PackagesGrid's TIER_TONES exactly so the two pages agree.
  platinum: { bar: "bg-accent" },
  gold: { bar: "bg-accent-magenta" },
  silver: { bar: "bg-accent-teal" },
  bronze: { bar: "bg-accent-orange" },
  supporting: { bar: "bg-accent-violet" },
  // Same red as its row partner `officialOutdoorTv`, on purpose: the two media
  // partners share one wall row (PAIRED_ROWS), and two matching bars side by
  // side read as one pair. No unused accent was left anyway.
  officialTvRadio: { bar: "bg-primary" },
  // No unused accent was left, so this shares the brand red with the Official
  // Bank. Both are category partners outside the paid ladder, which is what red
  // marks here, and — like `organisers`/`inKind` sharing navy — they sit far
  // apart on the wall, so they never read as one group. Its row partner
  // `officialTvRadio` shares it too, and there reading as one group IS the aim.
  officialOutdoorTv: { bar: "bg-primary" },
  // A category-exclusive partner, like the Official Bank, so it gets a tone of
  // its own rather than borrowing a paid-tier colour. Lime was the last unused
  // celebration accent. There is no unused accent left now, so the next new
  // tier will have to share one.
  officialHydration: { bar: "bg-accent-lime" },
  // In-kind sits outside the paid palette, so it takes the theme's secondary
  // navy — which is exactly what SponsorshipPage's INKIND_TONE already uses to
  // brand in-kind, keeping the two pages in agreement. It ties with
  // `organisers` on purpose; they sit at opposite ends of the wall, nine tiers
  // apart, so they never read as the same group.
  inKind: { bar: "bg-secondary" }
};

/*
 * PER-TIER SIZE LADDER — the share of the top rung's logo size each tier renders
 * at. This is what makes the wall state relative billing in pixels as well as in
 * words.
 *
 * The values are LINEAR multipliers on the logo's max-height, so rendered AREA
 * is the SQUARE of them. The rest of this file reasons in area, so read them
 * that way:
 *
 *     organisers / officialBank   1.00 linear   100% area   <- reference rung
 *     platinum                    0.92           85%
 *     gold                        0.83           69%
 *     silver                      0.75           56%
 *     bronze                      0.56           31%
 *     supporting / officialTvRadio /
 *       officialOutdoorTv /
 *       officialHydration / inKind
 *                                 0.45           20%
 *
 * Organisers and the Official Bank SHARE the top rung: neither sits on the paid
 * ladder (one runs the event, the other holds a category-exclusive naming
 * right), so neither outranks the other. In-kind ties with supporting for the
 * same reason from the other end — see its entry below. Official TV & Radio,
 * Official Outdoor TV and Official Hydration sit between those two, so the
 * ladder forces them to tie as well.
 *
 * ORDERING IS BY AREA ONLY — and that is a deliberate reversal, so read this
 * before "fixing" what looks like a bug.
 *
 * The wall used to guarantee that no logo in a lower tier exceeded a higher one
 * in EITHER dimension. That rule is arithmetically brutal here: equal area
 * splits between width and height by aspect ratio, so a SQUAT logo one tier down
 * still out-heights a WIDE logo one tier up unless pushed far smaller. DIALOG
 * (2.17:1, supporting) against Vistra (5.34:1, bronze) forces
 *     s_sup / s_bronze  <  sqrt(2.174 / 5.3403)  =  0.638
 * i.e. supporting <= 0.34, which is 12% of the top rung's AREA. The client read
 * that as "too small" twice, on both surfaces — so 12% area is simply not a
 * viable tier, whatever the geometry says.
 *
 * So: a lower tier now only has to be smaller in AREA. supporting sits at 0.45
 * (20% area), and the KNOWN, ACCEPTED consequence is that DIALOG renders about
 * 26% TALLER than Vistra despite being a tier below it. That is not a
 * regression, it is the trade — do not re-derive a dimension constraint to
 * "correct" it without asking the client, who chose this after seeing both.
 *
 * The silver -> bronze step (~0.75) is still steeper than the rest. That one WAS
 * derived under the old rule and is simply left as-is: it reads well and nothing
 * now forces it wider.
 *
 * Two earlier passes shipped a flatter ladder and the client read silver and
 * bronze as the same size, so DO NOT flatten this back without asking them.
 *
 * Empty tiers still get a rung, so they are right the moment they are filled.
 *
 * Unlike the ratio maths below, this is an EDITORIAL call about billing, so it
 * is hand-authored on purpose. To go back to a flat wall, set every value to 1 —
 * nothing else in this file has to change.
 */
const TIER_LOGO_SCALE: Record<SponsorTierId, number> = {
  organisers: 1.0,
  officialBank: 1.0,
  platinum: 0.92,
  gold: 0.83,
  silver: 0.75,
  bronze: 0.56,
  supporting: 0.45,
  /*
   * FORCED, not chosen — it sits between `supporting` and `officialOutdoorTv`,
   * both 0.45. It must also MATCH its row partner: the two render side by side
   * (PAIRED_ROWS), so any difference would be visible at a glance. To bill
   * Astro bigger, move BOTH media tiers up in SPONSOR_TIER_IDS together.
   */
  officialTvRadio: 0.45,
  /*
   * FORCED, not chosen — same reason as `officialHydration` below. The client
   * placed Official Outdoor TV after Supporting Partners (and Official TV &
   * Radio) and before Official Hydration, all 0.45, so 0.45 is the only value
   * the ladder guard accepts. To bill PowerScreen bigger, MOVE the tier up in
   * SPONSOR_TIER_IDS.
   */
  officialOutdoorTv: 0.45,
  /*
   * FORCED, not chosen. The client placed this tier between `officialOutdoorTv`
   * and `inKind`, which are both 0.45, and the ladder guard below allows no rung
   * bigger than the one above it. So 0.45 is the only valid value. If the
   * client wants F&N billed bigger, the tier has to MOVE UP in SPONSOR_TIER_IDS
   * (e.g. beside `officialBank`). Raising this number in place would trip the
   * guard.
   */
  officialHydration: 0.45,
  /*
   * TIED WITH `supporting` ON PURPOSE — this is a client instruction, not an
   * oversight, and it is the whole reason the ladder guard below permits equal
   * rungs rather than demanding a strict descent.
   *
   * In-kind is a different KIND of sponsorship (donated goods/services), not a
   * cheaper one, so it is billed alongside a supporting partner rather than
   * beneath one. It renders last purely because it sits outside the paid
   * ladder. Drop this below 0.45 and the wall starts asserting a hierarchy the
   * client explicitly did not ask for.
   */
  inKind: 0.45
};

/*
 * Show the unsold ladder tiers as dashed "slot available" tiles linking to
 * /sponsorship. OFF by default — an empty Platinum row on a credibility page
 * reads as a vacancy, not an invitation. Flip to true if the client wants the
 * open tiers advertised.
 */
const SHOW_EMPTY_TIER_SLOTS = false;

/*
 * Tiers that share ONE wall row, [left, right], split by a vertical grey rule.
 * Client request, 2026-09-26: the two media partners read as a pair, with
 * Official TV & Radio to the LEFT of Official Outdoor TV.
 *
 * The pair must be ADJACENT in SPONSOR_TIER_IDS, left first — toRows() only
 * merges neighbours, and a DEV guard below warns if that stops being true. If
 * either tier is empty (filtered out), the other simply renders as a normal
 * full-width row.
 *
 * Wall only. The footer strip already lays every group side by side and has no
 * dividers between any of them, so it needs nothing here.
 *
 * Below `sm` the pair STACKS (see PairedTierRow): at the wall's heading size
 * the two headings alone need ~432px, and a 375px phone has 343px.
 */
const PAIRED_ROWS: readonly (readonly [SponsorTierId, SponsorTierId])[] = [
  ["officialTvRadio", "officialOutdoorTv"]
];

/** Group tiers into wall rows: one tier per row, except a PAIRED_ROWS pair that sits side by side. */
function toRows(tiers: SponsorTier[]): SponsorTier[][] {
  const rows: SponsorTier[][] = [];
  for (const tier of tiers) {
    const prev = rows[rows.length - 1];
    const pairsWithPrev =
      prev?.length === 1 &&
      PAIRED_ROWS.some(([left, right]) => left === prev[0].id && right === tier.id);
    if (pairsWithPrev) prev.push(tier);
    else rows.push([tier]);
  }
  return rows;
}

type TileSize = "standard" | "compact";

/**
 * Outer width — drives how many logos land per row at each breakpoint.
 *
 * Every tier renders at `standard`. The TILE is one size everywhere and the
 * billing hierarchy is carried by the LOGO inside it (see TIER_LOGO_SCALE);
 * shrinking the boxes too would leave the lower tiers on a ragged grid and
 * re-measure the footer strip's scroller, for no gain in clarity.
 */
export const TILE_WIDTH: Record<TileSize, string> = {
  /*
   * Widths are set to the WRAP LIMIT at each band, because logo size is bought
   * with content width and nothing else (see LOGO_FILL). Measured headroom
   * against the widest row — Organisers, 4 logos — at the container width:
   *
   *   375px   container 343   4-up wraps to 2/row   -> 160 fits (2x160 + 16 gap)
   *   768px   container 736   3/row                 -> 224 fits (3x224 + 2x24)
   *   1024px  container 992   4/row                 -> 224 only (968 of 992)
   *   1280px  container 1184  4/row                 -> 278 MAX; 272 for margin
   *
   * lg stays at 14rem: at 1024 the 4-up Organisers row already uses 968 of
   * 992px, so there is nothing to take. The xl band is where the real gain is —
   * 1280+ was leaving ~280px of width unused.
   *
   * DO NOT push xl past 17.5rem: at exactly 1280 the container is 1184px, and
   * 4 x 288 + 3 x 24 = 1224 wraps the Organisers row — which would look broken,
   * since 1024 shows it 4-up.
   */
  standard: "flex w-[10rem] sm:w-[11.5rem] md:w-[14rem] lg:w-[14rem] xl:w-[17rem]",
  /*
   * The footer strip carries its groups on every page. That row is static and
   * finite: it centres while it fits and turns into a plain horizontal scroller
   * once it doesn't, so this width trades logo size against how many sponsors
   * land on screen before anyone has to scroll.
   *
   * 11rem at md+ was originally set against the TRACK BUDGET — it kept the
   * track at ~1176px inside a 1248px container at 1440, so the desktop footer
   * did not scroll. That headroom is SPENT: the strip now carries 20 tiles in 9
   * groups, and the track measured 3956px at md+ (3045px on mobile) on
   * 2026-10-14, after Malakoff joined Supporting — up from 3768 / 2905 with
   * Sime Darby Property, and 3580 / 2765 with the Official TV & Radio group
   * (Astro).
   * So it scrolls at EVERY breakpoint. (On mobile a single-tile group is as wide
   * as its LABEL, not its tile, so label copy moves the track there too.)
   * The client accepted that rather than shrink the tiles; the scroller,
   * chevrons and edge fades are a designed state, not a
   * fallback.
   *
   * If desktop must stop scrolling, THIS WIDTH IS THE LEVER — never the tier
   * ladder. But mind the shape of the trade: the compact tile is HEIGHT-bound
   * (its W/H exceeds the strip's rootSpanMax, so k clamps at 1), which means
   * roughly the first 3px of narrowing is free and everything past that shrinks
   * EVERY footer logo, existing sponsors included. Fitting all 9 inside 1248px
   * needs ~121px tiles, about a 31% cut. It is not a good trade at 9 tiles.
   */
  compact: "flex w-[8rem] sm:w-[8.5rem] md:w-[11rem]"
};

/**
 * Inner box height + padding.
 *
 * Padding is deliberately tight: it is the ONLY thing standing between the logo
 * and a bigger render. See LOGO_FILL — max logo area at the top rung is
 * contentWidth² / rootSpanMax², so every pixel of horizontal padding costs size
 * on both surfaces. The standard tile is px-1, not px-2, because with the cards
 * gone that padding draws nothing — the gap-4/gap-6 between tiles is what
 * actually separates neighbouring logos.
 *
 * HEIGHT IS NOT A LEVER while the tile is width-bound: k = (W/H)/rootSpanMax, so
 * the H in `k x H` cancels and the logo does not change size. It only starts to
 * matter once W/H exceeds rootSpanMax (~2.23) and k clamps at 1 — past that,
 * extra width is wasted instead. xl:h-32 exists to keep the wider xl box just
 * under that clamp so its extra width still counts.
 *
 * Keep in step with TILE_GEOM below, which restates these numbers so the scale
 * can be computed. Tailwind needs literal classes, hence the duplication.
 */
const TILE_BOX: Record<TileSize, string> = {
  standard: "h-24 px-1 py-1 md:h-28 xl:h-32",
  /*
   * The compact tile is HEIGHT-bound, the opposite of the wall: its W/H (2.11)
   * exceeds the strip's rootSpanMax (1.875, set by UOB), so k clamps at 1 and
   * WIDENING IT ALONE DOES NOTHING — measured +0%. Height is what moves the
   * logos here, up to H = W/rootSpan, past which the extra height is wasted
   * instead. Hence both grew together: 160x80 -> 176x96.
   */
  compact: "h-20 px-1 py-1 md:h-24"
};

/**
 * Border width TILE_BASE draws, in px. **0 since the cards were removed.**
 *
 * Kept as a named constant rather than deleted, because scaleFor() has to
 * subtract whatever the tile actually draws: box-sizing is border-box, so a
 * border eats into the content box and every k is derived from that box. If a
 * border ever comes back, change this WITH it — otherwise every logo is sized
 * against a content box 2px larger than reality and the whole set drifts.
 */
const TILE_BORDER = 0;

type Band = "base" | "sm" | "md" | "lg" | "xl";

/**
 * The numeric form of TILE_WIDTH + TILE_BOX, in px. One fact in two forms — the
 * classes are what render, this is what the scale is derived from, so THEY MUST
 * BE UPDATED TOGETHER. (Tailwind can't consume computed values and JS can't read
 * a class, so neither can be generated from the other.)
 */
const TILE_GEOM: Record<TileSize, Record<Band, { w: number; h: number; px: number; py: number }>> = {
  standard: {
    base: { w: 160, h: 96, px: 4, py: 4 }, //      w-[10rem] h-24 px-1 py-1
    sm: { w: 184, h: 96, px: 4, py: 4 }, //     sm:w-[11.5rem]
    md: { w: 224, h: 112, px: 4, py: 4 }, //     md:w-[14rem] md:h-28
    lg: { w: 224, h: 112, px: 4, py: 4 }, //     lg:w-[14rem] (1024 is full at 4-up)
    xl: { w: 272, h: 128, px: 4, py: 4 } //     xl:w-[17rem] xl:h-32
  },
  compact: {
    base: { w: 128, h: 80, px: 4, py: 4 }, //       w-[8rem] h-20 px-1 py-1
    sm: { w: 136, h: 80, px: 4, py: 4 }, //     sm:w-[8.5rem]
    md: { w: 176, h: 96, px: 4, py: 4 }, //     md:w-[11rem] md:h-24
    lg: { w: 176, h: 96, px: 4, py: 4 }, //     (no lg override — md carries on)
    xl: { w: 176, h: 96, px: 4, py: 4 } //     (no xl override either — track budget)
  }
};

/*
 * EQUAL OPTICAL AREA WITHIN A TIER — why the logos in one row are not all the
 * same height.
 *
 * A 5.3:1 wordmark (Vistra) and a 2.1:1 badge (DCD) cannot share a height and
 * look equal; the wordmark would tower over everything. What reads as "the same
 * size" is equal AREA, so that is what this computes — and then TIER_LOGO_SCALE
 * steps that area down, deliberately, from one tier to the next.
 *
 * Give the logo a max-height of `k / sqrt(ratio) * s` as a PERCENTAGE of the
 * tile's content height H, where s is the tier's scale. Rendered area is then
 *     (p*H)^2 * ratio  =  (k/sqrt(r) * s * H)^2 * r  =  k^2 * s^2 * H^2
 * — independent of ratio, so every logo in a tier renders at the same area, and
 * each tier renders at s^2 of the top rung. Because p is a percentage, both
 * hold at every breakpoint with no per-breakpoint px math.
 *
 * k is capped so the WIDEST RENDERED mark still fits the width. Per logo the
 * constraint is `k * sqrt(r) * s * H <= W`, so what binds is the largest
 * `sqrt(ratio) * tierScale` on the surface, not the largest raw ratio —
 * see maxRootSpan. Push k past that and the widest marks clamp on max-w-full
 * instead, which breaks the equality and the tier goes visibly uneven.
 *
 * NOTHING IN THIS DERIVATION IS HAND-TUNED. The spans come from the sponsor data
 * and the tier ladder, the caps come from TILE_GEOM, so adding a sponsor of ANY
 * aspect ratio to ANY tier re-derives every k, at every breakpoint, on both
 * surfaces, automatically. That is deliberate: an earlier version hardcoded
 * numbers against Vistra being the widest logo at 5.34:1, which would have
 * silently broken on the next wider sponsor. Adding a logo stays a
 * one-object-literal edit in data.ts.
 *
 * k is per breakpoint BAND because TILE_WIDTH and TILE_BOX change at different
 * breakpoints, so the tile's proportions (and therefore the cap) differ. Note md
 * is TIGHTER than sm for the standard tile — it grows taller but barely wider —
 * which is why sm and md need separate bands rather than sharing one.
 *
 * MARK SIZING (Sep 2026). A logo that carries a secondary element — a name
 * line UNDER its mark (Gas Malaysia, Malakoff, F&N, Public Bank), loops
 * AROUND it (UEM Sunrise) or a symbol BESIDE the wordmark (Axiata) — declares
 * its mark's box in data.ts. Sizing then measures the MARK, not the whole
 * file, and the image is shifted (--logo-dy) so the mark, not the image, is
 * centred on the tile;
 * the rest extends above, below or around it. Each marked logo also sets
 * opticalScale to its tier's median visible ink. All
 * of this goes through logoGeometry(), which reduces EXACTLY to the plain
 * whole-file maths when there is no mark — so unmarked logos are unaffected.
 * The width cap still sees the WHOLE image, so a marked logo can't width-clamp.
 * Accepted trade-off: marked logos read larger OVERALL than their tier-mates.
 *
 * THIS ONLY HOLDS IF EVERY FILE IS CROPPED TO ITS INK BOUNDS. The ratio is read
 * from the file, so baked-in whitespace is indistinguishable from the mark and
 * silently shrinks it. See the crop note at the top of data.ts.
 */

/**
 * The widest mark AS RENDERED in a set of tiers, as `sqrt(ratio) * tierScale`.
 * 1 if the set is empty.
 *
 * The tier scale belongs in here because the cap is a statement about what is
 * actually on screen. Leaving it out would still be SAFE — the cap would merely
 * be too tight — but it would hold every tier back on account of a logo that is
 * no longer the widest thing rendered. That is not hypothetical: the widest file
 * on the wall is Vistra at 5.34:1, and it sits in BRONZE, the smallest rung.
 * Including the scale hands roughly 4% back on the wall and 8% on the strip.
 */
/**
 * The geometry equal-area sizing works from. Normally the whole file; for a
 * sponsor with a `mark` (see data.ts) only that box, so a separate name line
 * underneath doesn't shrink the mark.
 *
 * `span` is the whole image's rendered width per unit of k·H·tierScale, and
 * `heightFactor` its rendered height: W/√(mw·mh) and H/√(mw·mh). With no mark
 * these are exactly the old √(W/H) and 1/√(W/H). `markCenterY` is the mark's
 * vertical centre as a fraction of the image height.
 */
function logoGeometry(s: Sponsor): { span: number; heightFactor: number; markCenterY: number } {
  const m = s.mark ?? { x: 0, y: 0, width: s.width, height: s.height };
  const root = Math.sqrt(m.width * m.height);
  return {
    span: s.width / root,
    heightFactor: s.height / root,
    markCenterY: (m.y + m.height / 2) / s.height
  };
}

function maxRootSpan(tiers: SponsorTier[]): number {
  const spans = tiers.flatMap((t) =>
    t.sponsors.map((s) => logoGeometry(s).span * TIER_LOGO_SCALE[t.id])
  );
  const max = Math.max(...spans);
  return Number.isFinite(max) && max > 0 ? max : 1;
}

/**
 * Widest logo actually rendered on each surface. Scoped per surface, not global:
 * the strip omits the organisers (see stripSponsorTiers), so a very wide
 * organiser logo must not shrink the footer.
 */
const ROOT_SPAN_MAX: Record<TileSize, number> = {
  standard: maxRootSpan(filledSponsorTiers),
  compact: maxRootSpan(stripSponsorTiers)
};

/**
 * How much of the geometric maximum to use. THE tuning knob — the one number to
 * change if the logos should be bigger or smaller overall.
 *   1.00 = the widest RENDERED logo exactly spans the content box edge to edge
 *   0.99 = a hair of slack, so subpixel rounding can never trip a width clamp
 *
 * A clamp is not cosmetic: it is what breaks equal area, so if a logo ever
 * renders width-clamped, LOWER THIS (0.98, then 0.97) rather than living with an
 * uneven tier. Verify by measuring rendered area in-browser — within any single
 * tier the spread must be 1.00x.
 *
 * (This used to also carry a proof that logos stayed clear of the ExternalLink
 * icon. That icon went with the cards, so the constraint is gone — the only
 * thing LOGO_FILL now guards is the width clamp.)
 */
const LOGO_FILL = 0.99;

/** Equal-area scale for one tile size at one breakpoint band, before the tier ladder. */
function scaleFor(size: TileSize, band: Band): number {
  const g = TILE_GEOM[size][band];
  const w = g.w - 2 * g.px - 2 * TILE_BORDER;
  const h = g.h - 2 * g.py - 2 * TILE_BORDER;
  // min(1, …) so a very short/wide tile can't push the logo past its own box height.
  return Math.min(1, w / h / ROOT_SPAN_MAX[size]) * LOGO_FILL;
}

/*
 * The widest rendered mark sets the pace for everyone on its surface. That is
 * correct — it is what keeps each tier even — but it means one much wider logo
 * legitimately shrinks the whole surface, which is surprising if you only added
 * one sponsor. Say so in dev rather than let someone hunt for it.
 */
if (import.meta.env.DEV) {
  // The old raw-ratio threshold, recast into root-span terms so it still means
  // "a 5.5:1 mark at the TOP rung". The same mark lower down the ladder renders
  // smaller and no longer binds — which is the whole point of the ladder.
  const DESIGN_RATIO = 5.5;
  const DESIGN_ROOT_SPAN = Math.sqrt(DESIGN_RATIO);
  for (const [size, span] of Object.entries(ROOT_SPAN_MAX)) {
    if (span > DESIGN_ROOT_SPAN) {
      console.warn(
        `[SponsorWall] The widest ${size} mark now renders as wide as a ${(span ** 2).toFixed(2)}:1 ` +
          `logo at the top rung (designed around ${DESIGN_RATIO.toFixed(2)}:1), so every logo on ` +
          `that surface is rendering smaller to stay even with it. Options: re-crop or use a ` +
          `stacked lockup for that sponsor, give it an opticalScale in data.ts, or accept the ` +
          `smaller set.`
      );
    }
  }

  /*
   * TIER ORDER GUARD — is the billing ladder itself well-formed?
   *
   * This USED to assert that no logo in a lower tier exceeded a higher one in
   * either dimension. That check is gone on purpose: ordering is now by AREA
   * only (see TIER_LOGO_SCALE), and a squat logo in a lower tier legitimately
   * renders TALLER than a wide one above it — DIALOG vs Vistra does exactly
   * that, by ~26%, and it is the accepted trade. Keeping the old check would
   * have fired on every page load, and a warning that is always on is a warning
   * nobody reads.
   *
   * What is left to verify is weaker but not worthless. Rendered area is
   * k^2 * s^2 * H^2, and k and H are common to a surface, so area ordering
   * reduces exactly to "TIER_LOGO_SCALE descends with billing rank". That is
   * true by construction — until someone fat-fingers a rung or inserts a tier in
   * the wrong place in SPONSOR_TIER_IDS, which is precisely the mistake worth
   * catching cheaply.
   *
   * Equal rungs are allowed (organisers and officialBank deliberately tie at
   * 1.00); only a rung that is BIGGER than the one above it is a fault.
   */
  {
    const rungs = SPONSOR_TIER_IDS.map((id) => ({ id, scale: TIER_LOGO_SCALE[id] }));
    for (let i = 1; i < rungs.length; i++) {
      const above = rungs[i - 1];
      const here = rungs[i];
      if (here.scale > above.scale) {
        console.warn(
          `[SponsorWall] LADDER OUT OF ORDER: "${here.id}" is set to ${here.scale}, LARGER than ` +
            `"${above.id}" (${above.scale}) which outranks it — so a lower tier would render ` +
            `bigger in area than a higher one. Fix the values in TIER_LOGO_SCALE, or the tier ` +
            `order in SPONSOR_TIER_IDS (data.ts) if the ranking itself changed.`
        );
      }
    }
  }

  /*
   * PAIRED ROW GUARD. toRows() only merges NEIGHBOURS, so a pair whose tiers
   * drift apart in SPONSOR_TIER_IDS (or swap order) silently stops sharing a
   * row. Say so rather than let the layout quietly change.
   */
  for (const [left, right] of PAIRED_ROWS) {
    const i = SPONSOR_TIER_IDS.indexOf(left);
    if (SPONSOR_TIER_IDS[i + 1] !== right) {
      console.warn(
        `[SponsorWall] PAIRED ROW BROKEN: "${left}" and "${right}" should share a wall row, but ` +
          `"${right}" does not come directly after "${left}" in SPONSOR_TIER_IDS (data.ts), so ` +
          `they render as two separate rows. Make them adjacent (left first) or drop the pair ` +
          `from PAIRED_ROWS.`
      );
    }
  }

  /*
   * MARK FIT GUARD. A marked logo renders its whole image larger than an
   * unmarked one of the same tier (the MARK gets the equal area; the name line
   * is extra), then shifts so the mark is centred. Warn if, at any band on
   * either surface, that whole image would leave the tile's content box.
   */
  const surfaces = [
    ["standard", filledSponsorTiers],
    ["compact", stripSponsorTiers]
  ] as const;
  for (const [size, tiers] of surfaces) {
    for (const tier of tiers) {
      for (const s of tier.sponsors) {
        if (!s.mark) continue;
        const { heightFactor, markCenterY } = logoGeometry(s);
        for (const band of Object.keys(TILE_GEOM[size]) as Band[]) {
          const g = TILE_GEOM[size][band];
          const cw = g.w - 2 * g.px - 2 * TILE_BORDER;
          const ch = g.h - 2 * g.py - 2 * TILE_BORDER;
          const imgH =
            scaleFor(size, band) * heightFactor * TIER_LOGO_SCALE[tier.id] * (s.opticalScale ?? 1) * ch;
          const imgW = (imgH * s.width) / s.height;
          const above = imgH * markCenterY;
          const below = imgH * (1 - markCenterY);
          if (imgW > cw + 0.5 || above > ch / 2 + 0.5 || below > ch / 2 + 0.5) {
            console.warn(
              `[SponsorWall] "${s.name}" (mark sizing) overflows its ${size} tile at ${band}: ` +
                `image ${imgW.toFixed(1)}x${imgH.toFixed(1)}px in a ${cw}x${ch}px box ` +
                `(${above.toFixed(1)}px above / ${below.toFixed(1)}px below centre). ` +
                `Re-check its mark box in data.ts, or drop the mark.`
            );
          }
        }
      }
    }
  }
}

/*
 * Emitted as CSS custom properties rather than one maxHeight, because the value
 * has to change at breakpoints and inline styles can't hold media queries. The
 * consuming classes (max-h-[var(--logo-mh)] etc.) are static strings, so
 * Tailwind still generates them — an interpolated arbitrary value would not.
 */
function logoScaleVars(sponsor: Sponsor, size: TileSize, tier: SponsorTierId): CSSProperties {
  const { heightFactor, markCenterY } = logoGeometry(sponsor);
  // The tier ladder and the per-sponsor nudge MULTIPLY, so a sponsor's nudge
  // stays relative to its own tier rather than to the top rung.
  const scale = TIER_LOGO_SCALE[tier] * (sponsor.opticalScale ?? 1);
  const pct = (band: Band) =>
    `${(scaleFor(size, band) * heightFactor * scale * 100).toFixed(2)}%`;
  const vars: Record<string, string> = {
    "--logo-mh": pct("base"),
    "--logo-mh-sm": pct("sm"),
    "--logo-mh-md": pct("md"),
    "--logo-mh-lg": pct("lg"),
    "--logo-mh-xl": pct("xl")
  };
  // Marked logos only: shift so the MARK's centre (not the image's) sits on the
  // tile centre, and the name line hangs below. translateY(%) is relative to
  // the image's own height, so one value holds at every breakpoint.
  if (sponsor.mark) vars["--logo-dy"] = `${((0.5 - markCenterY) * 100).toFixed(2)}%`;
  return vars as CSSProperties;
}

/*
 * NO CARD — the logo IS the element. The tile survives only as an invisible
 * layout box and, deliberately, as the HIT TARGET: at their tier scales several
 * marks are tiny (DCD renders ~19px tall inside a 94px box), so shrinking the
 * target down to the ink would hurt touch users and WCAG 2.5.8. Hovering
 * anywhere in the box is intended, not a bug.
 *
 * What replaces the card as the "this is clickable" signal:
 *   - cursor-pointer, set EXPLICITLY rather than inherited from the UA's <a>
 *     default. It already resolved to pointer, but with no card this is now the
 *     primary affordance and shouldn't rest on a browser default.
 *   - the hover lift here, plus a scale on the <img> in SponsorTile — a cursor
 *     does nothing for touch users, so there has to be a visible change too.
 *   - focus-visible ring for keyboard users.
 *
 * ring-offset-card, NOT -background: the surface behind these is now white (see
 * the section in SponsorWall), so a background-coloured offset would draw a
 * pale halo inside the ring.
 *
 * The white-matte constraint that used to force bg-card here has moved UP to
 * that section — see its comment before changing either.
 */
/*
 * THE WALL SITS ON PURE WHITE, AND THAT IS LOAD-BEARING — not a style choice.
 *
 * 8 of the 24 logo files are a hard WHITE MATTE rather than transparency (Mah
 * Sing, Vistra, UOB, The Edge Malaysia, Yayasan Bursa, DIALOG, LFG, and YTL,
 * whose matte is partial: a white plate behind its emblem only). While each
 * logo sat on its own white card that was invisible. With the cards gone,
 * anything but a white surface behind them paints eight white rectangles across
 * the wall — on the page's pale-sky #F4F8FC they are plainly visible.
 *
 * The other 16 split two ways. Fifteen have real alpha (Bursa, TEEF, SD
 * Guthrie, DCD, Axiata, Sime, Sime Darby Property, AEON, UEM Sunrise, F&N,
 * Gas Malaysia, Malakoff, Public Bank, PowerScreen, Astro). AEON was a magenta
 * plate until Sep 2026 and is now a plain wordmark. UEM Sunrise's white
 * "SUNRISE", F&N's white "F" and Sime Darby Property's white lettering are
 * opaque artwork enclosed by navy/black/red shapes, not mattes, so they don't
 * count against the surface. HIMALAYA SPORTS
 * IS THE ODD ONE OUT: a fully opaque near-black plate, no transparency
 * anywhere. It is unaffected by the surface colour and is the one logo a white
 * background does NOT flatter — see its entry in data.ts, where shipping it as
 * supplied is a recorded client decision.
 *
 * RE-COUNT THIS, don't trust the sentence: DIALOG and LFG were both missing
 * from the list above for a release, and DCD has flipped alpha -> matte ->
 * alpha across three artwork swaps.
 *
 * So: DO NOT tint this section, and audit any NEW logo's alpha before adding it
 * (`modal opaque colour == white` means matted). The alternative is re-keying
 * artwork the sponsors supplied, which haloes on anti-aliased edges — The Edge
 * Malaysia has teal edge pixels that fringe.
 *
 * bg-card (not bg-white) so it still follows the theme token, and full-bleed —
 * painting it on the `container` instead would leave pale margins at wide
 * viewports, i.e. one giant card, which is the opposite of the intent.
 */
const WALL_SURFACE = "bg-card";

const TILE_BASE =
  "group relative flex w-full cursor-pointer items-center justify-center rounded-lg " +
  "transition-transform duration-200 motion-safe:hover:-translate-y-0.5 " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring " +
  "focus-visible:ring-offset-2 focus-visible:ring-offset-card";

export function SponsorTile({
  sponsor,
  tier,
  size = "standard",
  eager = false
}: {
  sponsor: Sponsor;
  /**
   * The tier this logo is billed under — required, not defaulted, because a
   * silent fallback to the top rung would put a bronze logo at organiser size
   * and nothing would fail. Both call sites (the wall and the footer strip)
   * already have it to hand.
   */
  tier: SponsorTierId;
  size?: TileSize;
  eager?: boolean;
}) {
  const { t } = useTranslation("sponsors");

  const logo = (
    <img
      src={sponsor.logo}
      alt={sponsor.name}
      width={sponsor.width}
      height={sponsor.height}
      loading={eager ? "eager" : "lazy"}
      decoding="async"
      /*
       * Inline, not aspect-[w/h] / max-h-[x%] classes: both values are computed
       * from data and Tailwind arbitrary values can't be templated.
       *
       * aspectRatio is required, not cosmetic — Header.tsx documents the Safari
       * flexbox <img> bug where a wide image renders at near-natural size and
       * blows up the layout, and these tiles are flex containers with wide
       * <img> children. maxHeight is what equalises optical area within a tier
       * and steps it down between tiers (see LOGO_FILL and TIER_LOGO_SCALE);
       * max-w-full below is the backstop that clamps the widest marks on narrow
       * screens.
       */
      style={{
        aspectRatio: `${sponsor.width} / ${sponsor.height}`,
        ...logoScaleVars(sponsor, size, tier)
      }}
      /* The scale is the touch/visual half of the affordance — cursor-pointer on
         the link covers mice only. transform-only, so it can't reflow the row. */
      className={cn(
        "w-auto max-w-full object-contain transition-transform duration-200 motion-safe:group-hover:scale-[1.06] max-h-[var(--logo-mh)] sm:max-h-[var(--logo-mh-sm)] md:max-h-[var(--logo-mh-md)] lg:max-h-[var(--logo-mh-lg)] xl:max-h-[var(--logo-mh-xl)]",
        // Only marked logos move; Tailwind composes this with the hover scale.
        sponsor.mark && "translate-y-[var(--logo-dy)]"
      )}
    />
  );

  return (
    <a
      href={sponsor.href}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(TILE_BASE, TILE_BOX[size])}
    >
      {logo}
      {/* The alt text is the link's accessible name; this appends the new-tab
          warning to it (WCAG G201). No aria-label on the <a> — it would
          override the alt and leave the image unnamed. */}
      <span className="sr-only"> {t("logo.opensInNewTab")}</span>
      {/* No visible external-link icon since the cards were removed: it was
          anchored to the card's bottom-right corner, and with no card it floats
          in empty space — for a short mark like DCD, ~35px below the logo. The
          sr-only text above is what actually satisfies WCAG G201; the icon was
          the sighted-user nicety. */}
    </a>
  );
}

function TierHeading({ tier }: { tier: SponsorTier }) {
  const { t } = useTranslation("sponsors");
  return (
    <ScrollReveal className="flex flex-col items-center text-center">
      <span aria-hidden className={cn("h-1 w-12 rounded-full", TIER_TONES[tier.id].bar)} />
      <h2 className="mt-4 text-2xl md:text-3xl">{t(`tiers.${tier.id}`)}</h2>
    </ScrollReveal>
  );
}

/*
 * NOTE: still styled as a dashed CARD, which no longer matches the wall now
 * that the sponsor tiles have none. It is unreachable (SHOW_EMPTY_TIER_SLOTS is
 * false), so it is left as-is rather than redesigned speculatively — but it
 * needs restyling before that flag is ever flipped on.
 */
function EmptySlot() {
  const { t } = useTranslation("sponsors");
  return (
    <Link
      to="/sponsorship/"
      className="grid h-24 w-full place-items-center rounded-xl border-2 border-dashed border-border bg-muted/40 px-5 py-4 text-center transition-colors duration-200 hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:h-28"
    >
      <span>
        <ImagePlus aria-hidden className="mx-auto h-6 w-6 text-foreground/40" />
        <span className="mt-2 block text-sm text-muted-foreground">{t("emptySlot.title")}</span>
      </span>
    </Link>
  );
}

/**
 * One tier: its heading, then its logo list. Returned as a fragment so a
 * normal full-width row renders exactly the markup it always has.
 */
function TierBlock({ tier, eager }: { tier: SponsorTier; eager: boolean }) {
  const { t } = useTranslation("sponsors");
  const tierLabel = t(`tiers.${tier.id}`);

  return (
    <>
      <TierHeading tier={tier} />

      {/* Centred flex-wrap rather than a grid: a tier may hold one logo or
          eight, and a grid leaves a lone logo hard-left and a partial row with
          a hole. Wrapping centres every row at any count. */}
      <ScrollReveal.Group
        role="list"
        aria-label={tierLabel}
        className="mt-8 flex flex-wrap items-stretch justify-center gap-4 md:gap-6"
        stagger={0.07}
      >
        {tier.sponsors.map((sponsor) => (
          <ScrollReveal.Item key={sponsor.id} role="listitem" className={TILE_WIDTH.standard}>
            <SponsorTile sponsor={sponsor} tier={tier.id} eager={eager} />
          </ScrollReveal.Item>
        ))}

        {SHOW_EMPTY_TIER_SLOTS && tier.sponsors.length === 0 && (
          <ScrollReveal.Item role="listitem" className={TILE_WIDTH.standard}>
            <EmptySlot />
          </ScrollReveal.Item>
        )}
      </ScrollReveal.Group>
    </>
  );
}

/*
 * Two tiers on one row with a vertical grey rule between them — see
 * PAIRED_ROWS.
 *
 * From `sm` up it is a fit-content grid with `1fr auto 1fr` tracks. Sized to
 * fit its content, both `1fr` tracks resolve to the WIDER column's max-content,
 * so the columns come out equal and hug their content, and the rule sits
 * exactly on the page centre whatever the label lengths (BM included). A
 * `flex-1` split would instead stretch the pair across the whole container and
 * push the two logos ~600px apart at xl.
 *
 * Below `sm` the same three children stack, and the rule element turns into a
 * horizontal line. gap-14 (56px) on each side of it matches the mt-14/pt-14
 * spacing the wall puts between every other pair of tiers, so on a phone this
 * looks exactly like two ordinary rows.
 *
 * The rule is bg-border, the same token as the horizontal rules between tiers.
 * It stretches the full row height (bar + heading + logos) through
 * sm:items-stretch — which is load-bearing, not redundant: the base
 * items-center would otherwise carry into the grid and collapse the h-auto rule
 * to zero height.
 */
function PairedTierRow({
  left,
  right,
  eager
}: {
  left: SponsorTier;
  right: SponsorTier;
  eager: boolean;
}) {
  return (
    <div className="flex flex-col items-center gap-14 sm:mx-auto sm:grid sm:w-fit sm:grid-cols-[1fr_auto_1fr] sm:items-stretch sm:gap-x-12 md:gap-x-16">
      <div className="w-full">
        <TierBlock tier={left} eager={eager} />
      </div>
      <div aria-hidden className="h-px w-full bg-border sm:h-auto sm:w-px" />
      <div className="w-full">
        <TierBlock tier={right} eager={eager} />
      </div>
    </div>
  );
}

export function SponsorWall() {
  const { t } = useTranslation("sponsors");
  const tiers = SHOW_EMPTY_TIER_SLOTS ? sponsorTiers : filledSponsorTiers;

  // Fresh year, nothing announced yet — say so rather than render a hero, a
  // hole, then a CTA.
  if (tiers.length === 0) {
    return (
      <section className={WALL_SURFACE}>
        <div className="container py-16 md:py-20">
          <ScrollReveal className="mx-auto max-w-xl text-center">
            <h2 className="text-2xl md:text-3xl">{t("pending.title")}</h2>
            <p className="mt-3 text-muted-foreground">{t("pending.body")}</p>
          </ScrollReveal>
        </div>
      </section>
    );
  }

  return (
    <section className={WALL_SURFACE}>
      <div className="container py-16 md:py-20">
        {toRows(tiers).map((row, index) => (
          <div
            key={row.map((tier) => tier.id).join("+")}
            /* A rule between rows, mirroring the reference wall. Not the
               alternating bg-muted/30 band idiom — with up to six tiers that
               zebra-stripes the whole page. */
            className={
              index === 0 ? undefined : "mt-14 border-t border-border pt-14 md:mt-16 md:pt-16"
            }
          >
            {row.length === 2 ? (
              <PairedTierRow left={row[0]} right={row[1]} eager={index === 0} />
            ) : (
              <TierBlock tier={row[0]} eager={index === 0} />
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
